import React, { useEffect, useRef, useState } from 'react';
import { AuthConsumer } from 'contexts/auth-context';
import CQGlobalHeader from 'components/cq-global-header';
import CQModule from 'components/cq-module';
import CQWelcomeMat from 'components/cq-welcome-mat';
import BrandBand from '@salesforce/design-system-react/components/brand-band';
import PageHeader from '@salesforce/design-system-react/components/page-header';
import Icon from '@salesforce/design-system-react/components/icon';
import { useTranslation } from 'react-i18next';
import { CQTimeline } from 'components/cq-timeline';
import CQStencil from 'components/cq-stencil';
import CQSpinner from 'components/cq-spinner';
import { Button, IconSettings, Modal, PageHeaderControl} from '@salesforce/design-system-react';
import CQCurrentOrgInfoModal from 'components/cq-current-org-info-modal';
import CQSummaryReport from 'components/cq-summary-report';
import DefaultTaskEngine from 'tasks/task-engine';
import { IPastRun } from 'interfaces/IHistory.interface';
import { SFConnection, SfConnector } from 'common/sf-connector';
import { IApplicableModules, IInstalledPackage } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from 'app-constants';
import DefaultStorage from 'common/storage';
import { CQMgmtServiceProvider } from 'services/cq-mgmt.service';
import { IOrgInfo } from 'interfaces/IModal.interface';
import { useNavigate } from 'react-router-dom';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import { isRefreshModule } from 'common/module.utils';

let applicableModules:{
    allModules: IApplicableModules[],
    lastFailedModule: IApplicableModules,
    lastRun:IPastRun,
    lastSkippedModule: IApplicableModules,
    currentlyActiveModule: IApplicableModules | null;
    isFreshInstallation?: boolean;
    isPartialFresh?: boolean;
    installedPackages?: IInstalledPackage[];
    daVersion?: string;
    daReleaseDate?: string;
}

/**
 * Loads application data by fetching initialization data from the server.
 * Attempts to retrieve applicable modules via an API call and restores default storage.
 * 
 * @async
 * @function loadData
 * @returns {Promise<void>}
 * @throws {Error} Logs any errors that occur during data fetching to the console.
 * 
 * @remarks
 * This function uses Promise.all() to execute multiple async operations concurrently,
 * but only processes the response from the initialization endpoint.
 */
async function loadData() {
    try {
        const [dataResponse] = await Promise.all([
                SFConnection.getAxiosInstance().get(CQAppConstant.INITIALIZE),
                CQMgmtServiceProvider.checkInstalledPackagesWithVersions(),
                DefaultStorage.restore()
            ]);

        applicableModules = dataResponse.data;

    } catch (error) {
        console.error('[loadData] failed:', error);
        throw error;
    }
}
  


function CQMainScreen() {
    const {t} = useTranslation();
    const authContext = AuthConsumer();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [viewHistoryButton, setViewHistoryButton] = useState<boolean>(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
    const [currentModule, setCurrentModule] = useState<string>(CQAppConstant.MODULE_TYPES.UPDATES);
    const [isEmptyEvent, setIsEmptyEvent] = useState<boolean>(false);
    const [orgInfo, setOrgInfo] = useState<IOrgInfo>({ name: '', isSandbox: '', userName: '', profileName: '', fullName: '' });
    const [canAccessPushPortal, setCanAccessPushPortal] = useState<boolean>(false);
    const [lastRunCompletedModule, setLastRunCompletedModule] = useState<string>('');
    const [reloadMainScreenTrigger, setReloadMainScreenTrigger] = useState(0);
    // Key to remount CQModule and CQTimeline when data refreshes
    const [moduleKey, setModuleKey] = useState<number>(0);
    const lastProcessedReloadRef = useRef(0);
    // Last run id we reloaded for. Kept in the parent so it survives the child
    // remount a reload causes, preventing the same run from re-triggering reloads.
    const lastNotifiedRunIdRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const [showWelcomeMat, setShowWelcomeMat] = useState<boolean>(false);
    const [freshModule, setFreshModule] = useState<IApplicableModules | null>(null);
    const [isWaitingForFreshInstall, setIsWaitingForFreshInstall] = useState<boolean>(false);
    const [freshInstallError, setFreshInstallError] = useState<string>('');

    /**
     * Triggers a reload for a newly started run, deduped by run id so each run
     * start reloads once (children remount on reload and lose their local guards).
     *
     * @param runId - Stable run identifier (e.g. its startedOn timestamp).
     */
    function notifyRunStart(runId: string) {
        if (!runId || runId === lastNotifiedRunIdRef.current) {
            return;
        }
        lastNotifiedRunIdRef.current = runId;
        setReloadMainScreenTrigger(prev => prev + 1);
    }

    /**
     * Polls GET /status until the run is picked up (activeTask recorded), so reloading
     * /initialize doesn't race ahead of the local-runner and show nothing. Stops early if
     * the run fails before ever becoming active.
     *
     * @param maxAttempts - Number of polling attempts before giving up.
     * @returns The run id once active, or null if it failed/never became active in time.
     */
    async function waitForRunToStart(maxAttempts: number = CQAppConstant.WAIT_FOR_RUN_MAX_ATTEMPTS): Promise<string | null> {
        let intervalMs = CQAppConstant.WAIT_FOR_RUN_INITIAL_INTERVAL_MS;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            try {
                const status = await SFConnection.getAxiosInstance().get(CQAppConstant.STATUS);
                const currentState = status.data;
                const activeTaskRaw = currentState?.[CQAppConstant.CQ_DOCUMENT_LOG.ACTIVE_TASK];
                if (activeTaskRaw) {
                    const tasksRaw = currentState?.[CQAppConstant.CQ_DOCUMENT_LOG.CQ_TASKS];
                    const startedOn = tasksRaw ? JSON.parse(tasksRaw)[0]?.startedOn : undefined;
                    return startedOn ? String(startedOn) : String(activeTaskRaw);
                }
                // The run already reached a terminal state (failed, or completed so fast that
                // ACTIVE_TASK was written and cleared before our first poll). Stop waiting so
                // the caller can reload right away instead of burning through the full backoff
                // schedule - otherwise isWaitingForFreshInstall would stay true, leaving the UI
                // stuck on the spinner for the entire polling duration.
                if (currentState?.status === CQAppConstant.INSTALLATION_STATUSES.FAILED || currentState?.status === CQAppConstant.INSTALLATION_STATUSES.COMPLETED) {
                    return null;
                }
            } catch (error) {
                console.error('[MainScreen] Failed to poll fresh installation status:', error);
            }
            intervalMs = Math.min(intervalMs + CQAppConstant.INTERVAL_THRESHOLD, CQAppConstant.POLLING_MAX_INTERVAL);
        }
        return null;
    }

    // start loading the data if authenticated
    useEffect(() => {
        if (authContext.isAuthenticated) {
            setIsLoading(true);
            loadData().then(async () => {
                fetchData();
                checkPushPortalPermission();
                await fetchOrgAndUserDetails();
                // Check for fresh installation and set welcome mat state.
                // Skip showing the welcome mat if a Fresh installation is already
                // active or previously failed - those cases are resumed via the
                // normal running/failed module panel instead of restarting from scratch.
                const hasFreshInProgress = applicableModules?.currentlyActiveModule?.forModule === CQAppConstant.MODULE_TYPES.FRESH;
                const hasFreshFailed = applicableModules?.lastFailedModule?.forModule === CQAppConstant.MODULE_TYPES.FRESH;
                if (applicableModules?.isFreshInstallation && !hasFreshInProgress && !hasFreshFailed) {
                    activateFreshModule();
                }
                setIsLoading(false);
            }).catch((error: any) => {
                console.error('[CQMainScreen] loadData failed:', error instanceof Error ? error.message : JSON.stringify(error));
                const message = error?.response?.data?.error || (error instanceof Error ? error.message : CQAppConstant.ERROR_MESSAGES.UNKNOWN_ERROR);
                authContext.logout();
                navigate(`/?error=${encodeURIComponent(`${message} ${CQAppConstant.ERROR_MESSAGES.CONTACT_ADMINISTRATOR}`)}`, { replace: true });
            });
        }
    }, [authContext.isAuthenticated]);

    useEffect(() => {
        fetchData();
    }, [currentModule]);

    // Detect external reload requests and reinitialize data. Uses ref to ensure each trigger value is processed exactly once.
    useEffect(() => {
        async function reinitialize() {
            try {
                await loadData();
                fetchData();
                setModuleKey(key => key + 1);
                // Clear the waiting state once active, failed, or already finished (lastRun.endedOn) -
                // handles a Fresh run completing before either active/failed state is observed.
                if (applicableModules?.currentlyActiveModule || applicableModules?.lastFailedModule || applicableModules?.lastRun?.endedOn) {
                    setIsWaitingForFreshInstall(false);
                }
            } catch (error: any) {
                console.error('[CQMainScreen] reinitialize failed:', error instanceof Error ? error.message : JSON.stringify(error));
                // Do not logout mid-session — user can retry via the Refresh button
            }
        }

        if (reloadMainScreenTrigger !== lastProcessedReloadRef.current) {
            reinitialize();
            lastProcessedReloadRef.current = reloadMainScreenTrigger;
        }
    }, [reloadMainScreenTrigger]);

    /**
     * Finds and activates the fresh installation module.
     * Sets the fresh module state and shows the welcome mat if found.
     */
    function activateFreshModule() {
        const freshModuleEntry = applicableModules?.allModules?.find(
            (module: IApplicableModules) => module.moduleIdentifier === CQAppConstant.MODULE_TYPES.FRESH
        );
        if (freshModuleEntry) {
            setFreshModule(freshModuleEntry);
            setShowWelcomeMat(true);
        }
    }

    /**
     * Fetches the installed packages history using the DefaultTaskEngine.
     * If the history is available and lastFailedModule is empty, it updates the state to show the "View History" button.
     *
     * @async
     * @function fetchData
     * @returns {Promise<void>} A promise that resolves when the data fetching and state update are complete.
     */
    async function fetchData () {
        let history= await DefaultTaskEngine.readInstalledPackagesHistory();
        if (history && !applicableModules?.lastFailedModule) {
            setViewHistoryButton(true);
        } else {
            setViewHistoryButton(false);
        }
    }

/**
 * Fetches the current Salesforce organization and user details from SFConnection
 * and updates the component state with the retrieved information.
 *
 * The function retrieves organization details such as organization name and type
 * (Sandbox or Production), along with user details including username, full name,
 * and profile name.
 *
 * @async
 * @function fetchOrgAndUserDetails
 * @returns {Promise<void>} Resolves once the organization and user details
 * are successfully fetched and the state is updated.
 */
    async function fetchOrgAndUserDetails() {
        try{
            const orgData = await SFConnection.getOrgAndUserDetails();
            if(!orgData) return;
            setOrgInfo({ 
                name: orgData.orgName, 
                isSandbox: orgData.isSandbox ? CQAppConstant.ORG_TYPE.SANDBOX : CQAppConstant.ORG_TYPE.PRODUCTION, 
                userName: orgData.user.username, 
                profileName: orgData.user.profileName, 
                fullName: orgData.user.fullName 
            });
        }catch(error){
            console.error(error)
        }
    }

    /**
     * Checks if the current user has permission to access Push Portal.
     * Sets canAccessPushPortal state based on the permission check result.
     * On error, defaults to hiding the Push Portal button.
     */
    async function checkPushPortalPermission() {
        try {
            const permissions = await PushPortalServiceProvider.getPermissions();
            setCanAccessPushPortal(permissions.canView);
        } catch (error) {
            console.error('Failed to check Push Portal permissions:', error);
            setCanAccessPushPortal(false);
        }
    }

    /**
     * Handles the fresh installation flow triggered from the welcome mat.
     *
     * Builds the selectedModule and variables from the user's package/version
     * selections, posts to /executeTask, then polls until the run is picked up
     * by the local-runner before reloading. On any error the spinner is cleared,
     * the welcome mat is restored, and the error message is surfaced to the user.
     *
     * @async
     * @function handleFreshInstall
     * @param selectedModules - Map of packageName → whether the user selected it.
     * @param selectedVersions - Map of packageName → the version string to install.
     * @returns {Promise<void>}
     */
    async function handleFreshInstall(
        selectedModules: { [key: string]: boolean },
        selectedVersions: { [key: string]: string }
    ): Promise<void> {
        setShowWelcomeMat(false);
        setFreshInstallError('');
        setIsWaitingForFreshInstall(true);
        setCurrentModule(CQAppConstant.MODULE_TYPES.FRESH);

        // Build selectedModule from freshModule based on user selection
        const selectedExtensions = Object.keys(selectedModules).filter((packageName) => selectedModules[packageName]);
        const selectedFreshInstalls = freshModule?.applicableFreshInstalls?.filter(
            script => selectedExtensions.includes(script.packageName)
                && script.finalVersion === selectedVersions[script.packageName]
        ) || [];

        // Resolve the human-readable versionName (e.g. "CQ Summer '26") from the selected
        // fresh installs. All selected scripts share the same finalVersion, so the Core
        // package entry's versionName is authoritative. Falls back to the first selected
        // script if Core isn't found.
        const resolvedVersionName = selectedFreshInstalls.find(script => script.packageName === CQAppConstant.COMPLIANCEQUEST)?.versionName || selectedFreshInstalls[0]?.versionName;

        // Build the module with installationTasks (S3 steps already appended during /initialize)
        const selectedModule: IApplicableModules = {
            moduleIdentifier: CQAppConstant.MODULE_TYPES.FRESH,
            forModule: CQAppConstant.MODULE_TYPES.FRESH,
            installationTasks: selectedFreshInstalls.flatMap(script => script.steps || []).map(task => ({ ...task, isSelected: true })),
            applicableFreshInstalls: selectedFreshInstalls,
            inputs: freshModule?.inputs || [],
            versionName: resolvedVersionName
        };

        // Build variables from module inputs
        const variables: Record<string, any> = {};
        (selectedModule.inputs || []).forEach((input: any) => {
            variables[input.name] = input.default;
        });

        try {
            await SFConnection.getAxiosInstance().post(CQAppConstant.EXECUTE_TASK, {
                selectedModule,
                variables,
                isManualTaskCompleted: false,
                lastRun: false,
                readonlySkippedTasks: false,
                lastFailed: false,
                discardLastIncompleteRun: false
            });
            // The POST only queues the run with the task-scheduler - wait until the
            // local-runner actually starts it before reloading, otherwise /initialize
            // may still show no currentlyActiveModule.
            const runId = await waitForRunToStart();
            if (runId) {
                notifyRunStart(runId);
            } else {
                setIsWaitingForFreshInstall(false);
                // Fall back to a single reload so the user isn't stuck if the
                // run took longer than we polled for.
                setReloadMainScreenTrigger(prev => prev + 1);
            }
        } catch (error: any) {
            console.error('[MainScreen] Failed to start fresh installation:', error);
            setIsWaitingForFreshInstall(false);
            setShowWelcomeMat(true);
            setFreshInstallError(
                error?.response?.data?.error || error?.message || t('fresh_install_start_error')
            );
        }
    }

    /**
     * Renders the title section of the main screen.
     *
     * This function returns a JSX element containing a vertical grid layout
     * with a main header and a subheader. The main header displays a localized
     * string for the homepage page header, while the subheader shows the package
     * name and version (or a default "No package" message) along with additional
     * localized organization installation information.
     *
     * @returns {JSX.Element} A JSX element representing the title section.
     */
    function renderTitle() {
        const packageVersion = SfConnector.releaseVersionName ?? CQAppConstant.NO_PACKAGE;
        const userDisplay = `${orgInfo.userName || ''} / ${orgInfo.fullName || ''} / ${orgInfo.profileName || ''}`;

        return (
            <div className="slds-grid slds-grid_vertical">
                <h1>{t("homepage_pageheader")}</h1>

                <h2 className="slds-text-heading_small slds-m-vertical_xx-small">
                    {packageVersion} {t("org_install_info")}
                </h2>

                <p className="slds-text-body_small">
                    {t("org_name")} {orgInfo.name} <br />
                    {t("org_type")} {orgInfo.isSandbox} <br />
                    {t("org_login")} {userDisplay}
                </p>
            </div>
        );
    }

    const onRenderAction = () => {
        const sameModuleCheck = currentModule === lastRunCompletedModule;
        return (
            <PageHeaderControl>
                {lastRunCompletedModule && sameModuleCheck &&( 
                    <Button
                        label={currentModule === CQAppConstant.MODULE_TYPES.UPGRADES ? t("refresh_label", { type: currentModule }) : "Refresh"}
                        variant="brand"
                        iconName="refresh"
                        iconPosition="left"
                        onClick={() => window.location.reload()}
                    />
                )}
                { viewHistoryButton && !isRefreshModule(currentModule) && ( <Button label={t("view_history", {type: currentModule })} variant="brand" onClick={() => setIsReportModalOpen(!isReportModalOpen)} />)}
                { canAccessPushPortal && (
                    <Button
                        label={t("push_portal_pageheader")}
                        variant="brand"
                        iconCategory="utility"
                        iconName="announcement"
                        iconPosition="left"
                        onClick={() => navigate('/push-portal')}
                    />
                )}
                <CQCurrentOrgInfoModal modalOpenState={isModalOpen} toggleModalState={() => setIsModalOpen(!isModalOpen)} packageNameAndVersion = {SfConnector.releaseVersionName} orgInfo={orgInfo} /> 
            </PageHeaderControl>
        );
    }

    return (
        <>
            <IconSettings iconPath="/assets/icons">
            { authContext.isAuthenticated ?
                (isLoading ?
                    <CQStencil /> :
                    <>
                        <CQGlobalHeader></CQGlobalHeader>
                        <div style={{marginTop: "3rem"}}>
                            <BrandBand
                                id="brand-band-lightning-blue"
                                className="slds-p-around_small"
                            >
                                <PageHeader
                                    icon={
                                        <Icon
                                            assistiveText={{
                                                label: 'Home',
                                            }}
                                            category="utility"
                                            name="setup"
                                            colorVariant="warning"
                                            title="Home"
                                        />
                                    }
                                    onRenderActions={onRenderAction}
                                    title={renderTitle()}
                                    variant="object-home"
                                />
                                
                                {isWaitingForFreshInstall ? (
                                    <div className="slds-grid slds-align_absolute-center slds-m-top_large">
                                        <CQSpinner />
                                    </div>
                                ) : !showWelcomeMat && (
                                    <div className="slds-grid slds-scrollable_none">
                                        <div className={`slds-col slds-size_${isEmptyEvent ? 12 : 8}-of-12`}>
                                            <div className="slds-box slds-theme_default slds-var-m-top_medium">
                                                <CQModule key={moduleKey} applicableModules={applicableModules} setViewHistoryButton={setViewHistoryButton} setCurrentModule={setCurrentModule} orgInfo={orgInfo} setLastRunCompletedModule={setLastRunCompletedModule} notifyRunStart={notifyRunStart} onSelectFreshPackages={activateFreshModule}></CQModule>
                                            </div>
                                        </div>
                                        <div className="slds-col slds-size_4-of-12">
                                            <div className="slds-box slds-theme_default  slds-var-m-left_small slds-var-m-top_medium">
                                                <CQTimeline key={moduleKey} currentModule={currentModule} setIsEmptyEvent={setIsEmptyEvent}></CQTimeline>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {showWelcomeMat && freshModule && (
                                    <CQWelcomeMat
                                        freshModule={freshModule}
                                        orgInfo={orgInfo}
                                        errorMessage={freshInstallError}
                                        isPartialFresh={applicableModules?.isPartialFresh}
                                        installedPackages={applicableModules?.installedPackages}
                                        onInstall={handleFreshInstall}
                                        onCloseMat={() => {
                                            setShowWelcomeMat(false);
                                            setFreshInstallError('');
                                        }}
                                    />
                                )}
                                    
                            </BrandBand>
                        
                        </div>
                    </>
                ) : <></>
            }
            <>
                <Modal
                    isOpen={isReportModalOpen}
                    onRequestClose={ ( ) => setIsReportModalOpen(!isReportModalOpen)}
                    size="medium"
                    footer={[
                        <Button label={t("close")} onClick={ ( ) => setIsReportModalOpen(!isReportModalOpen)} />,
                    ]}
                >
                    <div className='slds-m-around_large'>
                        <CQSummaryReport isViewHistoryClicked={true} currentModule={currentModule}/>
                    </div>
                </Modal>
            </>
            </IconSettings>
        </>
    );
}

export default CQMainScreen;